import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
// import { useTranslation } from 'react-i18next'; // Not used - using useLanguage hook instead
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../hooks/useLanguage';
import colors from '../theme/colors';
import { IconGlobe, IconLock } from './icons';

const SwitcherContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 8px;

  /* Mobile: relative positioning when inside ButtonsContainer */
  @media (max-width: 768px) {
    position: static;
  }
`;

const LanguageButton = styled(motion.button)`
  background: ${colors.base.themePrimary};
  border: 1px solid ${colors.border.light};
  border-radius: 20px;
  color: ${colors.text.light};
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  box-shadow: ${colors.effects.shadow};
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${colors.button.aboutBgHover};
    border-color: ${colors.border.lightHover};
    transform: translateY(-1px);
    box-shadow: ${colors.effects.shadowHover};
  }

  &:active {
    transform: translateY(0);
  }
`;

// Privacy policy link — styled like the language button (required by Google OAuth verification)
const PrivacyLink = styled(motion.a)`
  background: ${colors.base.themePrimary};
  border: 1px solid ${colors.border.light};
  border-radius: 20px;
  color: ${colors.text.light};
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  box-shadow: ${colors.effects.shadow};
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;

  &:hover {
    background: ${colors.button.aboutBgHover};
    border-color: ${colors.border.lightHover};
    transform: translateY(-1px);
    box-shadow: ${colors.effects.shadowHover};
  }
`;

const DropdownMenu = styled(motion.div)`
  position: fixed;
  background: ${colors.base.themePrimaryTransparent};
  border: 1px solid ${colors.border.lightHover};
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  display: flex;
  flex-direction: row;
  gap: 0;
  z-index: 15000;
`;

const MenuItem = styled(motion.button)`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.95);
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;
  text-align: center;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-weight: ${props => props.$active ? '700' : '600'};
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  &:not(:last-child) {
    border-right: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const Arrow = styled(motion.span)`
  font-size: 10px;
`;

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'EN', flag: '🇬🇧', fullName: 'English' },
  { code: 'fr', label: 'FR', flag: '🇫🇷', fullName: 'Français' }
];

const LanguageSwitcher = () => {
  const { currentLanguage, changeLanguage, isLanguageLoaded } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  // Calculate dropdown position when menu opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updateDropdownPosition = () => {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom + 8,
          left: rect.left
        });
      };
      
      updateDropdownPosition();
      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('scroll', updateDropdownPosition, { passive: true });
      
      return () => {
        window.removeEventListener('resize', updateDropdownPosition);
        window.removeEventListener('scroll', updateDropdownPosition);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  if (!isLanguageLoaded) {
    return null; // Ne pas afficher le bouton tant que la langue n'est pas chargée
  }

  const currentLangOption = LANGUAGE_OPTIONS.find(lang => lang.code === currentLanguage) || LANGUAGE_OPTIONS[0];

  const handleLanguageSelect = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <>
      <SwitcherContainer ref={containerRef}>
        <LanguageButton
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <IconGlobe size={14} /> {currentLangOption.label}
          <Arrow
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            ▼
          </Arrow>
        </LanguageButton>
        <PrivacyLink
          href="/privacy.html"
          title={currentLanguage?.startsWith('fr') ? 'Politique de confidentialité' : 'Privacy Policy'}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <IconLock size={14} /> {currentLanguage?.startsWith('fr') ? 'Confidentialité' : 'Privacy'}
        </PrivacyLink>
      </SwitcherContainer>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <DropdownMenu
              style={dropdownPos}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {LANGUAGE_OPTIONS.map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  $active={currentLanguage === lang.code}
                  whileHover={{ x: 4 }}
                >
                  {lang.flag} {lang.fullName}
                </MenuItem>
              ))}
            </DropdownMenu>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default LanguageSwitcher;